/**
 * shouldQueueFailedWrite — the single decision for whether a FAILED online
 * write should be QUEUED for offline replay rather than hard-failing the rep.
 *
 * FR-FE-OFFLINE-FIRST-WRITE-STANDARD-2026-06-09 §I (operator, verbatim):
 *   "Steffen drives in the border region to France with constant connection
 *    drops [German/French network competition]. The app must NEVER block
 *    merely because there is no connection at submit time. Save locally first,
 *    show 'gespeichert – wird synchronisiert', push when online, sync when back
 *    home on WLAN. Fail loud ONLY if local-persist itself fails, or a queued
 *    item is ultimately undeliverable."
 *
 * In the field this surfaces as, and ALL of these are replayable → QUEUE:
 *   - NO HTTP status — a transport / timeout / dropped-connection failure
 *     (our serviceLayerClient wraps these as ServiceHttpError('INTERNAL' /
 *     'TIMEOUT') with no `.status`; iOS Safari reports "Load failed", which the
 *     old `name==='TypeError' || message.includes('fetch')` check MISSED).
 *   - 401 — serviceFetch already spent its ONE refresh-and-retry, so the rep must
 *     log in again. Not an input defect: the entry is kept and sent by the first
 *     flush after the login (the replay layer holds it as 'auth_required').
 *   - 408 request-timeout, 429 rate-limit, 5xx server — server-transient.
 *
 * EVERY OTHER 4xx IS FINAL (TASK-FE-V8-DOOR-OWNERSHIP-REFUSALS-2026-10-08, AC 7):
 * 400, 403, 404, 409, 422 … — the server evaluated this exact body and said no, and
 * the source_event_id makes a re-send the same request, so it gets the same answer.
 * A 403 is a refusal on a VALID session (ownership — ENGAGEMENT_NOT_OWNED, the v8
 * door's SL #2682 codes), not a session blip; a 409 is not resolved by re-sending.
 * Also FINAL: a deterministic upstream cause hidden behind a transient outer status
 * (a 502 …_RPC_FAILED whose envelope carries a Postgres class-23 SQLSTATE like
 * 23514, or a 4xx downstream code — BUG-2026-07-21-S1787), and a
 * `ContractMismatchError`. A plain 502 with NO structured upstream stays
 * replayable — it is a genuine transient RPC blip. A final online submit is
 * re-thrown to the door's sink; a visit report is kept pinned on the device
 * (durableSubmit), every other door's entry is removed.
 *
 * Queueing is never silent loss: the offline queue is critical-by-default
 * (never purged, retried until synced) and an ultimately-undeliverable entry is
 * surfaced LOUDLY by the OfflineIndicator failed-banner with a manual retry.
 *
 * SSoT: this delegates to `isRetryable` (retryClassification.ts) — the SAME
 * classifier the replay loop uses, so the "queue it?" decision at submit and
 * the "retry it?" decision on replay can never drift: no-status / 401 / 408 /
 * 429 / 5xx → queue; every other 4xx, a deterministic upstream cause, and a
 * `ContractMismatchError` (blind replay can never fix envelope drift) → final.
 */
import { isRetryable } from './retryClassification'

export function shouldQueueFailedWrite(err: unknown): boolean {
  return isRetryable(err)
}
